import { FORM_ROOT_CLASS } from '~/lib/FormCssApi';
import {
    formFontFamily,
    formThemeProperties,
    formThemeTokens,
    type FormStyles,
} from '~/lib/FormTheme';
import { BRIDGE_SCRIPT } from './sandboxBridge';
import type {
    CustomQuestionConfirmation,
    CustomQuestionDefinition,
    CustomQuestionModeName,
} from './CustomQuestionApi';

/**
 * Builds the document a custom question runs in.
 *
 * It is handed to an iframe as srcdoc with sandbox="allow-scripts" and
 *  nothing else - in particular not allow-same-origin, which is what keeps the
 *  frame on an opaque origin where it cannot reach our cookies, our storage or
 *  the page around it. Everything the question needs comes in through the
 *  bridge instead.
 *
 * The code itself is stored in our own database and inlined here, so a form
 *  renders exactly the version that was published. The question is free to
 *  load scripts and call APIs from inside the frame - see
 *  docs/custom-questions.md for what that means for the business.
 */

export type SandboxDocumentOptions = {
    definition: CustomQuestionDefinition;
    mode: CustomQuestionModeName;
    token: string;
    settings: Record<string, any>;
    value?: any;
    answeredAt?: string | null;
    styles?: FormStyles | null;
    questionId?: number | string | null;
    /** The form's own custom CSS, already sanitized server side */
    customCss?: string | null;
    /** The submission being confirmed, on a confirmation section only */
    confirmation?: CustomQuestionConfirmation | null;
};

/**
 * The frame's own policy.
 *
 * External scripts and requests are deliberately allowed: a custom question is
 *  meant to be able to pull in a library and talk to an API. What stays shut
 *  is the handful of things that would let the document stop being a question
 *  in a box - submitting itself somewhere, or re-pointing every relative URL
 *  in it.
 *
 * Worth knowing: a srcdoc document inherits the embedding page's policy as
 *  well, and the two are intersected. Ours cannot loosen the page's, so if the
 *  app's own CSP (config/csp.php, report-only today) is ever enforced it will
 *  need to allow what custom questions load.
 */
const FRAME_CSP = [
    'default-src * data: blob:',
    "script-src * data: blob: 'unsafe-inline' 'unsafe-eval'",
    "style-src * data: 'unsafe-inline'",
    'img-src * data: blob:',
    'font-src * data:',
    'connect-src *',
    'media-src * data: blob:',
    'frame-src *',
    "form-action 'none'",
    "base-uri 'none'",
].join('; ');

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * JSON for embedding in a script tag. `</script>` inside a string would end
 *  the tag early, and U+2028/9 are newlines to a JS parser but not to JSON.
 */
function toScriptJson(value: any): string {
    let json: string;

    try {
        json = JSON.stringify(value === undefined ? null : value);
    } catch {
        json = 'null';
    }

    return (json ?? 'null')
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');
}

/**
 * Enough styling that a question looks like the rest of the form before its
 *  author writes a single rule. The theme arrives as the same custom
 *  properties the form itself uses.
 */
function baseStyles(styles?: FormStyles | null): string {
    return `
:root {
    ${formThemeProperties(styles)}
    --breely-radius: 6px;
    --breely-border: #d1d5db;
    --breely-text: #374151;
    --breely-muted: #6b7280;
}

* { box-sizing: border-box; }

html, body {
    margin: 0;
    padding: 0;
    background: transparent;
    color: var(--breely-text);
    font-family: ${formFontFamily(styles)};
    font-size: var(--breely-font-size);
    line-height: 1.45;
    /* The host sizes the frame to the content, so the document never scrolls */
    overflow: hidden;
}

a { color: var(--breely-color); }

.breely-label {
    display: block;
    font-size: var(--breely-font-size-sm);
    color: var(--breely-text);
    margin-bottom: 4px;
}

.breely-input {
    width: 100%;
    padding: 8px 12px;
    font: inherit;
    color: inherit;
    background: #fff;
    border: 1px solid var(--breely-border);
    border-radius: var(--breely-radius);
}

.breely-input:focus {
    outline: none;
    border-color: var(--breely-color);
    box-shadow: 0 0 0 1px var(--breely-color);
}

.breely-button {
    font: inherit;
    padding: 8px 16px;
    color: #fff;
    background: var(--breely-color);
    border: 1px solid var(--breely-color);
    border-radius: 999px;
    cursor: pointer;
}

.breely-button:hover { background: var(--breely-color-light); border-color: var(--breely-color-light); }

.breely-button-light {
    font: inherit;
    padding: 8px 16px;
    color: var(--breely-color);
    background: var(--breely-color-lightest);
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
}

.breely-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid var(--breely-border);
    border-radius: var(--breely-radius);
    margin-bottom: 6px;
    cursor: pointer;
}

.breely-option.breely-selected {
    border-color: var(--breely-color);
    color: var(--breely-color);
    background: var(--breely-color-lightest);
}

.breely-error {
    color: #dc2626;
    font-size: var(--breely-font-size-sm);
    margin-top: 4px;
}

[hidden] { display: none !important; }

body[data-breely-mode='answer'] .breely-input,
body[data-breely-mode='readonly'] .breely-input {
    background: #f9fafb;
}

/* A question that doesn't draw anything still needs to be clickable-sized */
body { min-height: 1px; }
`;
}

export function buildSandboxDocument({
    definition,
    mode,
    token,
    settings,
    value,
    answeredAt,
    styles,
    questionId,
    customCss,
    confirmation,
}: SandboxDocumentOptions): string {
    const theme = formThemeTokens(styles);

    // The body carries the form root class and the question's attributes, so
    //  the business's own form CSS reaches a custom question the same way it
    //  reaches every other one
    const bodyAttributes = [
        `class="${FORM_ROOT_CLASS}"`,
        `data-breely-mode="${escapeHtml(mode)}"`,
        `data-breely-question-type="custom"`,
        questionId
            ? `data-breely-question="${escapeHtml(`${questionId}`)}"`
            : '',
        `data-breely-custom-question="${definition.id}"`,
    ]
        .filter(Boolean)
        .join(' ');

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${escapeHtml(FRAME_CSP)}">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="referrer" content="no-referrer">
<title>${escapeHtml(definition.name || 'Question')}</title>
<style>${baseStyles(styles)}</style>
${customCss ? `<style>${customCss}</style>` : ''}
<style>${definition.css || ''}</style>
</head>
<body ${bodyAttributes}>
${definition.html || ''}
<script>
window.__BREELY_TOKEN__ = ${toScriptJson(token)};
window.__BREELY_MODE__ = ${toScriptJson(mode)};
window.__BREELY_SETTINGS__ = ${toScriptJson(settings)};
window.__BREELY_VALUE__ = ${toScriptJson(value)};
window.__BREELY_ANSWERED_AT__ = ${toScriptJson(answeredAt ?? null)};
window.__BREELY_THEME__ = ${toScriptJson(theme)};
window.__BREELY_CONFIRMATION__ = ${toScriptJson(confirmation ?? null)};
</script>
<script>${BRIDGE_SCRIPT}</script>
<script>
${definition.js || ''}
</script>
</body>
</html>`;
}
